import { Skeleton, Stack, styled } from '@mui/material';
import React, { isValidElement, useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';

type GenericFormProps<FormValues extends FieldValues> = {
  formMethods: ReturnType<typeof useForm<FormValues>>;
  children: React.ReactNode | React.ReactNode[];
  loading?: boolean;
} & React.ComponentProps<typeof Stack>;

const GenericFormContainer = styled(Stack, {
  name: 'GenericFormContainer',
})(({ theme }) => ({
  '& .Mui-error .MuiInput-input:not(:placeholder-shown)': {
    color: theme.palette.error.main,
  },
}));

export const GenericForm = <T extends FieldValues>({
  formMethods,
  children,
  loading,
  ...rest
}: GenericFormProps<T>) => {
  const [showSkeleton, setShowSkeleton] = useState(false);

  useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (loading) {
      setShowSkeleton(true);
      timeout = setTimeout(() => setShowSkeleton(false), 500);
    } else {
      setShowSkeleton(false);
    }
    return () => clearTimeout(timeout);
  }, [loading]);

  const renderedChildren =
    isValidElement(children) && children.type === React.Fragment
      ? React.Children.map(
          (children.props as { children: React.ReactNode }).children,
          (fragmentChild, index) =>
            isValidElement(fragmentChild) ? (
              // it's React.Fragment now but we can change it to any other component if needed
              <React.Fragment
                key={(fragmentChild.props as { name?: string })?.name || `child-${index}`}
              >
                {fragmentChild}
              </React.Fragment>
            ) : null,
        )
      : children;
  return (
    <FormProvider {...formMethods}>
      <GenericFormContainer gap={2} {...rest}>
        {showSkeleton
          ? Array.from({
              length: Array.isArray(renderedChildren) ? renderedChildren.length : 1,
            }).map((_, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Skeleton key={index} width="100%" height={50} />
            ))
          : renderedChildren}
      </GenericFormContainer>
    </FormProvider>
  );
};
